import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { announcementAPI } from '../services/api';
import { Announcement } from '../types';
import './AnnouncementDetail.css';

const AnnouncementDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      fetchAnnouncement(slug);
    }
  }, [slug]);

  const fetchAnnouncement = async (slug: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await announcementAPI.getBySlug(slug);
      setAnnouncement(response.data.announcement);
    } catch (error: any) {
      console.error('Duyuru yükleme hatası:', error);
      if (error.response?.status === 404) {
        setError('Duyuru bulunamadı');
      } else {
        setError('Duyuru yüklenirken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <>
        <section className="screen screen-blog">
          <div className="panel-content">
            <h1>Duyuru Detayı</h1>
          </div>
        </section>
        <section className="screen-tail screen-tail--blog">
          <div className="tail-container">
            <div className="announcement-detail-loading">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  if (error || !announcement) {
    return (
      <>
        <section className="screen screen-blog">
          <div className="panel-content">
            <h1>Duyuru Bulunamadı</h1>
          </div>
        </section>
        <section className="screen-tail screen-tail--blog">
          <div className="tail-container">
            <div className="announcement-detail-error">
              <i className="bi bi-exclamation-triangle"></i>
              <h2>{error || 'Duyuru bulunamadı'}</h2>
              <p>Aradığınız duyuru mevcut değil veya kaldırılmış olabilir.</p>
              <Link to="/announcements" className="btn-back">
                <i className="bi bi-arrow-left"></i>
                Duyurular Sayfasına Dön
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <section className="screen screen-blog">
        <div className="panel-content">
          <h1>{announcement.title}</h1>
        </div>
      </section>

      <section className="screen-tail screen-tail--blog" aria-label="Duyuru detayı">
        <div className="tail-container">
          <div className="announcement-detail">
            {/* Back Button */}
            <div className="announcement-detail-nav">
              <button onClick={() => navigate(-1)} className="btn-back">
                <i className="bi bi-arrow-left"></i>
                Geri Dön
              </button>
              <Link to="/announcements" className="btn-all">
                <i className="bi bi-list-ul"></i>
                Tüm Duyurular
              </Link>
            </div>

            {/* Content Card */}
            <article className="announcement-detail-card">
              {/* Header */}
              <header className="announcement-detail-header">
                <h1 className="announcement-detail-title">{announcement.title}</h1>
                <div className="announcement-detail-meta">
                  <div className="meta-item">
                    <i className="bi bi-calendar3"></i>
                    <span>{formatDate(announcement.created_at)}</span>
                  </div>
                  {announcement.author && (
                    <div className="meta-item">
                      <i className="bi bi-person"></i>
                      <span>{announcement.author.name} {announcement.author.surname}</span>
                    </div>
                  )}
                  <div className="meta-item">
                    <i className="bi bi-tag"></i>
                    <span className="announcement-badge">Duyuru</span>
                  </div>
                </div>
              </header>

              {/* Divider */}
              <div className="announcement-detail-divider"></div>

              {/* Content */}
              <div className="announcement-detail-content">
                <p>{announcement.content}</p>
              </div>

              {/* Footer */}
              <footer className="announcement-detail-footer">
                <div className="share-section">
                  <span className="share-label">
                    <i className="bi bi-share"></i>
                    Paylaş:
                  </span>
                  <div className="share-buttons">
                    <button
                      onClick={() => {
                        const url = window.location.href;
                        navigator.clipboard.writeText(url);
                        alert('Link kopyalandı!');
                      }}
                      className="share-btn"
                      title="Linki Kopyala"
                    >
                      <i className="bi bi-link-45deg"></i>
                    </button>
                    <a
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(announcement.title)}&url=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-btn"
                      title="Twitter'da Paylaş"
                    >
                      <i className="bi bi-twitter"></i>
                    </a>
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-btn"
                      title="Facebook'ta Paylaş"
                    >
                      <i className="bi bi-facebook"></i>
                    </a>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(announcement.title + ' - ' + window.location.href)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="share-btn"
                      title="WhatsApp'ta Paylaş"
                    >
                      <i className="bi bi-whatsapp"></i>
                    </a>
                  </div>
                </div>
              </footer>
            </article>

            <div className="announcement-detail-actions">
              <Link to="/announcements" className="btn-view-all">
                <i className="bi bi-grid-3x3-gap"></i>
                Diğer Duyuruları Gör
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AnnouncementDetail;
